import React, {useCallback, useState} from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Image,
  TouchableOpacity,
  Platform,
  UIManager,
  KeyboardAvoidingView,
  LayoutAnimation,
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Snackbar,
  Divider,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {launchImageLibrary} from 'react-native-image-picker';
import {useRegistrationStore} from '../../store/useRegistrationStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {useAuthStore} from '../../store/useAuthStore';
import {colors, spacing} from '../../theme';
import StepIndicator from '../../components/StepIndicator';

if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const ProgressBar = ({step, total, t}) => (
  <View style={styles.progressContainer}>
    <Text style={styles.progressText}>
      {t('store.stepOf', {step, total})}
    </Text>
    <View style={styles.progressBarTrack}>
      <View
        style={[styles.progressBarFill, {width: `${(step / total) * 100}%`}]}
      />
    </View>
  </View>
);

const SectionHeader = ({title}) => (
  <Text style={styles.sectionHeader}>{title}</Text>
);

const schema = yup.object().shape({
  name: yup.string().required('Store name is required'),
  phone: yup.string(),
  email: yup.string().email('Invalid email'),
  website: yup.string(),
  instagram: yup.string(),
  whatsappPhone: yup.string(),
});

const StoreSetupStep1Screen = ({navigation}) => {
  const {t} = useTranslation();
  const {ownerName, phone, countryCode, email} = useRegistrationStore();
  const createOrgUnit = useOrgUnitStore(state => state.createOrgUnit);
  const uploadOrgUnitImages = useOrgUnitStore(state => state.uploadOrgUnitImages);
  const setHasStore = useAuthStore(state => state.setHasStore);

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [logoImage, setLogoImage] = useState(null);
  const [photoImage, setPhotoImage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: ownerName ? `${ownerName}'s Shop` : '',
      phone: phone ? countryCode + phone : '',
      email: email || '',
      website: '',
      instagram: '',
      whatsappPhone: '',
    },
  });

  const pickImage = useCallback(async (setter, previewSetter) => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.8,
    });
    if (!result.didCancel && result.assets?.[0]) {
      setter(result.assets[0]);
      previewSetter(result.assets[0].uri);
    }
  }, []);

  const handleNext = async () => {
    const valid = await trigger(['name']);
    if (valid) {
      const data = getValues();
      const state = navigation.getState();
      const routeNames = state.routeNames || [];
      const nextScreen = routeNames.includes('RegisterStep5')
        ? 'RegisterStep5'
        : 'StoreSetupStep2';
      navigation.navigate(nextScreen, {
        storeName: data.name.trim(),
        storePhone: data.phone.trim(),
        storeEmail: data.email.trim(),
        whatsappPhone: data.whatsappPhone.trim(),
        website: data.website.trim(),
        instagram: data.instagram.trim(),
        logoImage,
        photoImage,
      });
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await createOrgUnit({
        name: ownerName ? `${ownerName}'s Shop` : 'My Shop',
        code: 'MAIN',
        orgType: 'OUTLET',
      });
      setHasStore(true);
    } catch (err) {
      setSnackbar(err.response?.data?.error?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const renderImagePicker = (label, preview, onPick) => (
    <TouchableOpacity style={styles.imagePicker} onPress={onPick}>
      {preview ? (
        <Image source={{uri: preview}} style={styles.imagePreview} />
      ) : (
        <View style={styles.imagePlaceholder}>
          <Text style={styles.imagePlaceholderIcon}>+</Text>
          <Text style={styles.imagePlaceholderText}>{label}</Text>
          <Text style={styles.tapToUpload}>{t('store.tapToUpload')}</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView
          style={styles.form}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <StepIndicator currentStep={4} />
          <ProgressBar step={1} total={2} t={t} />

          <SectionHeader title={t('store.storeInfo')} />

          {/* Store Name */}
          <Controller
            control={control}
            name="name"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.name') + ' *'}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={!!errors.name}
                mode="outlined"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />

          <Divider style={styles.divider} />

          {/* Contact Fields */}
          <SectionHeader title={t('store.contactDetails')} />

          <Controller
            control={control}
            name="phone"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.phone')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                keyboardType="phone-pad"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
                left={<TextInput.Icon icon="phone" />}
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.email')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={!!errors.email}
                mode="outlined"
                style={styles.input}
                keyboardType="email-address"
                autoCapitalize="none"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
                left={<TextInput.Icon icon="email" />}
              />
            )}
          />

          <Controller
            control={control}
            name="website"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.website')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                keyboardType="url"
                autoCapitalize="none"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
                left={<TextInput.Icon icon="web" />}
              />
            )}
          />

          <Controller
            control={control}
            name="instagram"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.instagram')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                autoCapitalize="none"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
                left={<TextInput.Icon icon="instagram" />}
              />
            )}
          />

          <Controller
            control={control}
            name="whatsappPhone"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('store.whatsapp')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                keyboardType="phone-pad"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
                left={<TextInput.Icon icon="whatsapp" />}
              />
            )}
          />

          {/* Image Pickers */}
          <Divider style={styles.divider} />
          <SectionHeader title={t('store.storeImages')} />
          <View style={styles.imageRow}>
            {renderImagePicker(t('store.storeLogo'), logoPreview, () =>
              pickImage(setLogoImage, setLogoPreview),
            )}
            {renderImagePicker(t('store.storePhoto'), photoPreview, () =>
              pickImage(setPhotoImage, setPhotoPreview),
            )}
          </View>

          {Object.keys(errors).length > 0 && (
            <Text style={styles.errorText}>
              {errors[Object.keys(errors)[0]]?.message}
            </Text>
          )}

          <Button
            mode="contained"
            onPress={handleNext}
            style={styles.actionButton}
            buttonColor={colors.primary}
            contentStyle={styles.buttonContent}>
            {t('common.next')}
          </Button>

          <Button
            mode="text"
            onPress={handleSkip}
            loading={saving}
            disabled={saving}
            style={styles.skipButton}
            textColor={colors.textSecondary}>
            {t('registration.skipForNow')}
          </Button>
        </ScrollView>
      </KeyboardAvoidingView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}
        action={{label: t('common.ok'), onPress: () => setSnackbar('')}}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {padding: spacing.md},
  progressContainer: {
    marginBottom: spacing.lg,
  },
  progressText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    fontWeight: '500',
  },
  progressBarTrack: {
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  divider: {
    marginVertical: spacing.md,
  },
  imageRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  imagePicker: {
    flex: 1,
    height: 150,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    overflow: 'hidden',
    backgroundColor: colors.surface,
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  imagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholderIcon: {
    fontSize: 32,
    color: colors.textLight,
  },
  imagePlaceholderText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  tapToUpload: {
    fontSize: 11,
    color: colors.textLight,
    marginTop: 2,
  },
  errorText: {color: colors.error, marginBottom: spacing.sm, fontSize: 12},
  actionButton: {
    marginTop: spacing.md,
    borderRadius: 8,
  },
  buttonContent: {
    paddingVertical: spacing.xs,
  },
  skipButton: {
    marginTop: spacing.sm,
    marginBottom: spacing.xxl,
  },
});

export default StoreSetupStep1Screen;
